import { Component, Element, Event, EventEmitter, h, Host, Prop, State } from "@stencil/core";
import pfx from "../../../utils/style-class-prefix-helper";
import { isSafari } from "../../../utils/device-helper";
import sanitizeHtml from "sanitize-html";
import { allowClassesAndLinks } from "../../../utils/sanitize-helper";
import { StyledComponent } from "../../../decorators/styled-component";

// import { InitLogging } from "../../../decorators/init-logging";

/**
 * @slot info-icon - Holds a single eon-ui-info-icon element
 * @slot html-label - contents of the label element
 */
@Component({
  tag: "envc-eon-ui-radio-button",
  styleUrl: "radio-button.scss",
  shadow: false
})
export class RadioButton {
  // @InitLogging() sentryTransaction;
  inputElement: HTMLInputElement;
  radioButtonGroup: HTMLEnvcEonUiRadioButtonGroupElement;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEnvcEonUiRadioButtonElement;

  /**
   * Raised after the RadioButton has been clicked.
   */
  @Event() radioButtonClicked: EventEmitter<string>;

  /**
   * The label that appears on the right hand side of the radio-button. The label is a string that gives a description on what the value of the radio-button means.
   * If you need HTML support, provide the label via the slot "html-label".
   * @example <eon-ui-radio-button label="Agree to terms of service"/>
   */
  @Prop() label: string;

  /**
   * Define the size of the input element
   */
  @Prop() size: "small" | "normal" = "normal";

  /**
   * The name Attribute is a string that refers to the native name attribute of an HtmlInputElement.
   * @example <eon-ui-radio-button name="group1"/>
   */
  @Prop() name: string;

  /**
   * The tabIndex Attribute is a number that gives the Element its RadioButton its tabindex if it is not disabled in a RadioButtonGroup.
   * The default is 0;
   */
  @Prop() index: number = 0;

  /**
   * Wether or not the radio-button is disabled. Disabled radio-buttones behave like readonly native html-radio-buttones.
   * See [MDN Docs - radio-button](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/Input/radio-button) for the readonly attribute.
   * The color of the labels and error is also changed to indicate this state.
   * @example <eon-ui-radio-button label="undo-time" disabled>
   */
  @Prop({ reflect: true, mutable: true }) disabled: boolean = false;

  /**
   * Wether or not the radio-button is checked. Changes if the radio-button is clicked.
   * @example <eon-ui-radio-button label="I want this to be checked from the beginning" checked />
   */
  @Prop({ reflect: true, mutable: true }) initiallyChecked: boolean = false;

  /**
   * Native html value attribute. Changes the key the radio-button value is stored in form submissions.
   * See [MDN Docs - radio-button](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/Input/radio-button)
   * @example <eon-ui-radio-button value="use-experimental-api" label="Do you want to use the experimental api?">
   */
  @Prop() value: string;

  /**
   * Whether this button can be focused by keyboard. Defaults to true.
   */
  @Prop() navigable: boolean = true;

  /**
   * **Internal use only! Please don't use this property directly.**
   * &nbsp;<br>
   * Set the input to invalid
   */
  @Prop({ reflect: true, mutable: true }) invalid: boolean = false;

  /**
   * Determine if the link should be displayed as if it was hovered.
   */
  @Prop() forceHoverStyle: boolean;

  @State() isInfoIconAvailable: boolean;

  /**
   * indicates the checked radiobutton
   */
  @Prop({ reflect: true, mutable: true }) checked: boolean = false;

  /**
   * Specifies the label to use for accessibility.
   */
  @Prop({ attribute: "a11y-aria-label", mutable: true }) a11yAriaLabel: string;

  componentWillLoad() {
    this.radioButtonGroup = this.hostElement.parentElement as HTMLEnvcEonUiRadioButtonGroupElement;
    this.isInfoIconAvailable = !!this.hostElement.querySelector('[slot="info-icon"]');
  }

  deprecatedFallback() {
    this.a11yAriaLabel = this.hostElement.getAttribute("aria-label") ?? this.a11yAriaLabel;
  }

  private handleChange() {
    this.checked = true;
  }

  render() {
    return (
      /*
       * In any other Component we won't class Scope the Host Element. Nevertheless in RadioButton we do that
       * cause we set Shadow Dom explicity to false to keep the native state sharing between the RadioButtons enabled.
       * To make the Buttons aware of a shadow-dom again and encapsulate it from document , we're setting the shadow-dom
       * of the wrapper Component RadioButtonGroup to true again.
       * So any RadioButton is always aware of the State of its Sister-Elements. Therefore we can use native behaviors and need no explicitly state exchange.
       */
      <Host>
        <div class={pfx({ "radio-button-wrapper": true, [this.size]: this.size })}>
          <label
            class={pfx({
              "radio-button-label": true,
              disabled: this.disabled,
              checked: this.checked,
              error: this.invalid,
              "force-hover": this.forceHoverStyle
            })}
          >
            <input
              ref={(el) => (this.inputElement = el as HTMLInputElement)}
              class={pfx({ "native-control": true, safari: isSafari() })}
              type="radio"
              name={this.name}
              value={this.value}
              disabled={this.disabled}
              onChange={() => this.handleChange()}
              checked={this.checked}
              {...(this.a11yAriaLabel && { "aria-label": this.a11yAriaLabel })}
            />
            <div class={pfx("focus-outline")}></div>
            <div class={pfx("radio-button")}></div>
            <span
              class={pfx({
                text: true,
                disabled: this.disabled
              })}
              innerHTML={sanitizeHtml(
                !this.hostElement.querySelector('[slot="html-label"]') ? this.label : undefined,
                allowClassesAndLinks
              )}
            >
              <slot name="html-label"></slot>
            </span>
          </label>
          {this.isInfoIconAvailable && (
            <span class={pfx("radio-button-info-icon")}>
              <slot name="info-icon" />
            </span>
          )}
        </div>
      </Host>
    );
  }
}
